import { UserOnboardingStages, UserGender } from '@app/graphql'
import { OddsFormat } from '@app/types'
import { CachedUserData } from '@app/helpers/userCache'

const USER_CACHE_KEY = 'vb_user_cache'
const CACHE_VERSION = '1'
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

/**
 * Populates a user state object from cached user data
 */
export const populateStateFromCache = (state: any, cachedUser: CachedUserData): void => {
  state.id = cachedUser.id
  state.name = cachedUser.firstName
  state.surname = cachedUser.lastName
  state.username = cachedUser.displayName
  state.userEmail = cachedUser.emailAddress
  state.phone = cachedUser.phoneNumber
  state.birthday = cachedUser.dateOfBirth ? new Date(cachedUser.dateOfBirth) : null
  state.countryId = cachedUser.countryId
  state.userType = cachedUser.role
  state.onboardingStage = cachedUser.onboardingStage as unknown as UserOnboardingStages
  state.avatarUrl = cachedUser.avatarUrl
  state.profileBackgroundUrl = cachedUser.background
  state.gender = cachedUser.gender as unknown as UserGender
  state.bio = cachedUser.bio
  state.unitShare = cachedUser.unitShare
  state.unitValue = cachedUser.unitValue
  state.oddsFormat = (cachedUser.oddsFormat as OddsFormat) ?? OddsFormat.Decimal
  state.authBySocial = true
  state.ownedChannels = cachedUser.channelsWhereUserIsTipster?.map(({ id, logo, channelName }) => ({
    id,
    channelName,
    logo,
  })) ?? []
  state.followedChannels = cachedUser.followedChannels ?? []
  state.hasPendingSubscription = cachedUser.hasPendingSubscription ?? false
  if (cachedUser.onboardingProgress) {
    const { __typename, ...data } = cachedUser.onboardingProgress
    state.tourStage = data
  }
}

/**
 * Initializes user state from localStorage cache (synchronous)
 * Returns true if state was successfully initialized from cache
 */
export const initializeUserStateFromCache = (state: any): boolean => {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const cachedUserJson = localStorage.getItem(USER_CACHE_KEY)
    if (!cachedUserJson) {
      return false
    }

    const cachedUser = JSON.parse(cachedUserJson)

    // Quick validation
    if (!cachedUser || !cachedUser.id || cachedUser.version !== CACHE_VERSION) {
      return false
    }

    // Check cache expiry
    const age = Date.now() - cachedUser.cachedAt
    if (age > CACHE_EXPIRY_MS) {
      return false
    }

    // Populate state from cache
    populateStateFromCache(state, cachedUser)
    return true
  } catch (error) {
    console.error('Failed to initialize user state from cache:', error)
    return false
  }
}
